import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { subscriptionAPI } from "@/lib/api";
import { toast } from "sonner";
import { 
  Check, X, Crown, Truck, Wrench, Camera, Users, Package, 
  BarChart3, FileText, Share2, Clipboard, MapPin, Car, Bell,
  Loader2, CreditCard, Key
} from "lucide-react";

// PayPal SVG Icon
const PayPalIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.65h6.603c2.203 0 3.922.543 4.97 1.57.974.951 1.396 2.207 1.288 3.838-.017.258-.055.524-.114.798-.617 2.88-2.728 4.637-5.508 4.637h-2.13c-.382 0-.717.253-.819.617l-.958 4.283a.378.378 0 0 1-.373.302H7.076zm6.328-15.343H8.698l-1.84 9.75h1.848l.635-2.874c.102-.364.437-.617.819-.617h2.13c2.78 0 4.891-1.757 5.508-4.637.059-.274.097-.54.114-.798.108-1.631-.314-2.887-1.288-3.838-.549-.537-1.265-.944-2.14-1.178a7.41 7.41 0 0 0-1.08-.155z"/>
  </svg>
);

const FEATURE_ICONS = {
  vehicles: Truck,
  mods: Wrench,
  services: Wrench,
  photos: Camera,
  unlimited_vehicles: Truck,
  teams: Users,
  parts_inventory: Package,
  analytics: BarChart3,
  pdf_export: FileText,
  public_sharing: Share2,
  custom_templates: Clipboard,
  shop_finder: MapPin,
  vin_decoder: Car,
  reminders: Bell,
  notifications: Bell,
};

export default function Subscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState(null);
  const [unlockCode, setUnlockCode] = useState("");
  const [applyingCode, setApplyingCode] = useState(false);

  useEffect(() => {
    loadSubscriptionData();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadSubscriptionData = async () => {
    try {
      const [statusRes, featuresRes] = await Promise.all([
        subscriptionAPI.status(),
        subscriptionAPI.features()
      ]);
      setSubscription(statusRes.data);
      setFeatures(featuresRes.data);
    } catch (error) {
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please check your email for confirmation.");
      return;
    }

    setCheckingPayment(true);

    try {
      const { data } = await subscriptionAPI.checkoutStatus(sessionId);
      
      if (data.payment_status === "paid") {
        toast.success(data.message || "Payment successful! Pro features activated.");
        if (data.unlock_code) {
          toast.info(`Your unlock code: ${data.unlock_code}`);
        }
        // Clear URL params and reload data
        navigate("/subscription", { replace: true });
        loadSubscriptionData();
        setCheckingPayment(false);
        return;
      } else if (data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        navigate("/subscription", { replace: true });
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      toast.error("Error checking payment status");
      setCheckingPayment(false);
    }
  };

  const handleUpgrade = async (paymentMethod = "stripe") => {
    try {
      setLoading(true);
      const originUrl = window.location.origin;
      
      let data;
      if (paymentMethod === "paypal") {
        const response = await subscriptionAPI.checkoutPayPal(originUrl);
        data = response.data;
      } else {
        const response = await subscriptionAPI.checkout(originUrl);
        data = response.data;
      }
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  const handleApplyCode = async () => {
    if (!unlockCode.trim()) {
      toast.error("Please enter an unlock code");
      return;
    }

    setApplyingCode(true);
    try {
      const { data } = await subscriptionAPI.applyUnlockCode(unlockCode.trim());
      toast.success(data.message);
      setUnlockCode("");
      loadSubscriptionData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid unlock code");
    } finally {
      setApplyingCode(false);
    }
  };

  if (loading && !features) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const isPro = subscription?.is_pro;

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-white flex items-center justify-center gap-2">
            <Crown className="h-8 w-8 text-amber-500" />
            Subscription Plans
          </h1>
          <p className="text-zinc-400">
            Choose the plan that fits your off-road adventure needs
          </p>
        </div>

        {/* Payment Status Check */}
        {checkingPayment && (
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="flex items-center justify-center gap-3 py-6">
              <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
              <span className="text-amber-500">Verifying payment...</span>
            </CardContent>
          </Card>
        )}

        {/* Current Status */}
        {isPro && (
          <Card className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/50">
            <CardContent className="flex items-center justify-between py-6">
              <div className="flex items-center gap-3">
                <Crown className="h-8 w-8 text-amber-500" />
                <div>
                  <p className="text-lg font-semibold text-white">Pro Subscriber</p>
                  <p className="text-sm text-zinc-400">
                    Expires: {new Date(subscription.expires_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <Badge className="bg-amber-500 text-black">ACTIVE</Badge>
            </CardContent>
          </Card>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Free Plan */}
          <Card className={`bg-zinc-900/50 border-zinc-800 ${!isPro ? 'ring-2 ring-zinc-600' : ''}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl text-white">Free</CardTitle>
                {!isPro && <Badge variant="outline" className="border-zinc-600">Current</Badge>}
              </div>
              <CardDescription>
                <span className="text-3xl font-bold text-white">$0</span>
                <span className="text-zinc-500"> / forever</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <FeatureItem available={true} limit="1">Vehicle Management</FeatureItem>
                <FeatureItem available={true}>Build Sheet / Mods</FeatureItem>
                <FeatureItem available={false}>Maintenance Tracking</FeatureItem>
                <FeatureItem available={false}>Photo Galleries</FeatureItem>
                <FeatureItem available={false}>Team Collaboration</FeatureItem>
                <FeatureItem available={false}>Parts Inventory</FeatureItem>
                <FeatureItem available={false}>Cost Analytics</FeatureItem>
                <FeatureItem available={false}>PDF Export</FeatureItem>
              </ul>
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card className={`bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/50 ${isPro ? 'ring-2 ring-amber-500' : ''}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl text-white flex items-center gap-2">
                  <Crown className="h-5 w-5 text-amber-500" />
                  Pro
                </CardTitle>
                {isPro && <Badge className="bg-amber-500 text-black">Current</Badge>}
              </div>
              <CardDescription>
                <span className="text-3xl font-bold text-white">${features?.pro_price || 49.99}</span>
                <span className="text-zinc-500"> / year</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <FeatureItem available={true}>Unlimited Vehicles</FeatureItem>
                <FeatureItem available={true}>Build Sheet / Mods</FeatureItem>
                <FeatureItem available={true}>Maintenance Tracking</FeatureItem>
                <FeatureItem available={true}>Photo Galleries</FeatureItem>
                <FeatureItem available={true}>Team Collaboration</FeatureItem>
                <FeatureItem available={true}>Parts Inventory</FeatureItem>
                <FeatureItem available={true}>Cost Analytics</FeatureItem>
                <FeatureItem available={true}>PDF Export</FeatureItem>
                <FeatureItem available={true}>Public Sharing</FeatureItem>
                <FeatureItem available={true}>Custom Templates</FeatureItem>
                <FeatureItem available={true}>Shop Finder</FeatureItem>
                <FeatureItem available={true}>VIN Decoder</FeatureItem>
                <FeatureItem available={true}>Service Reminders</FeatureItem>
              </ul>

              {!isPro && (
                <div className="space-y-3 pt-2">
                  <Button
                    onClick={() => handleUpgrade("stripe")}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                    disabled={loading}
                    data-testid="upgrade-stripe-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Pay with Card
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleUpgrade("paypal")}
                    variant="outline"
                    className="w-full border-blue-500 text-blue-400 hover:bg-blue-500/10 font-semibold"
                    disabled={loading}
                    data-testid="upgrade-paypal-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <PayPalIcon />
                        <span className="ml-2">Pay with PayPal</span>
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Unlock Code Section */}
        {!isPro && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Key className="h-5 w-5 text-amber-500" />
                Have an Unlock Code?
              </CardTitle>
              <CardDescription>
                Enter your unlock code to activate Pro features
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  value={unlockCode}
                  onChange={(e) => setUnlockCode(e.target.value.toUpperCase())}
                  className="bg-zinc-800 border-zinc-700 font-mono"
                  data-testid="unlock-code-input"
                />
                <Button
                  onClick={handleApplyCode}
                  disabled={applyingCode || !unlockCode.trim()}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="apply-code-btn"
                >
                  {applyingCode ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Apply"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <div className="text-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-zinc-400 hover:text-white"
          >
            Back to Garage
          </Button>
        </div>
      </div>
    </div>
  );
}

function FeatureItem({ available, limit, children }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      {available ? (
        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
      ) : (
        <X className="h-4 w-4 text-zinc-600 flex-shrink-0" />
      )}
      <span className={available ? "text-zinc-300" : "text-zinc-600"}>
        {children}
        {limit && <span className="text-zinc-500 ml-1">(up to {limit})</span>}
      </span>
    </li>
  );
}
